import { useId, useRef, useState } from "react";
import { useOnClickOutsideClose } from "src/hooks/useOnClickOutsideClose";
import { BaseCheckbox } from "../BaseCheckbox";
import { MultiSelectHeaderAction, MultiSelectOption } from "./multiSelect-types";

type Props = {
  options: MultiSelectOption[];
  selectedValues: string[];
  onToggleOption: (value: string) => void;
  /** 최상단 고정 액션. 필터형은 1개, 값 입력형은 2개를 넘깁니다. */
  headerActions?: MultiSelectHeaderAction[];
  /** 트리거에 표시할 텍스트. 빈 문자열이면 placeholder 를 노출합니다. */
  displayText: string;
  placeholder?: string;
  isDisabled?: boolean;
  className?: string;
};

/**
 * 멀티셀렉트 공용 룩.
 * 트리거 필드, 드롭다운 레이어, 최상단 고정 액션 행, 체크박스 목록만 담당합니다.
 * '전체' 같은 도메인 규칙과 선택값의 의미는 MultiSelectFilter / MultiSelectValue 가 정합니다.
 *
 * 항목 수 4~20개가 기본 적용 범위입니다. 51개 이상은 이 컴포넌트가 아니라
 * 범위 지정이나 그룹 트리 같은 별도 패턴을 사용합니다.
 */
const MultiSelectBox = ({ options, selectedValues, onToggleOption, headerActions = [], displayText, placeholder, isDisabled, className }: Props) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  // 같은 화면에 여러 개가 놓여도 체크박스 id 가 겹치지 않도록 인스턴스마다 접두사를 둡니다.
  const instanceId = useId();

  useOnClickOutsideClose(wrapRef, () => setIsMenuOpen(false));

  const onClickHeaderAction = (action: MultiSelectHeaderAction) => {
    action.onClick();
    if (action.closeOnClick) setIsMenuOpen(false);
  };

  return (
    <div className={className ? `base-multi-select-wrap ${className}` : "base-multi-select-wrap"} ref={wrapRef}>
      <div
        className={isDisabled ? "base-multi-select base-multi-select__disabled" : "base-multi-select"}
        onClick={() => !isDisabled && setIsMenuOpen((prev) => !prev)}
      >
        {placeholder && displayText === "" && <p className="base-multi-select__placeholder">{placeholder}</p>}
        <p className="base-multi-select__value">{displayText}</p>
        <div className="base-multi-select__chevron"></div>
      </div>
      {isMenuOpen && (
        <div className="base-multi-select__menu">
          {headerActions.length > 0 && (
            <div className="base-multi-select__menu__actions">
              {headerActions.map((action) => (
                <button
                  key={action.label}
                  type="button"
                  className="base-multi-select__menu__action"
                  disabled={action.isDisabled}
                  onClick={() => onClickHeaderAction(action)}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
          <ul className="base-multi-select__menu__list">
            {options.map((option) => (
              <li key={option.value} className="base-multi-select__menu__menu-list">
                <BaseCheckbox
                  wrap
                  id={`${instanceId}-${option.value}`}
                  name={option.label}
                  value={option.value}
                  label={option.label}
                  checked={selectedValues.includes(option.value)}
                  disabled={option.isDisabled}
                  onChange={() => onToggleOption(option.value)}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MultiSelectBox;
